<template>
  <RvRentalsHero
    :rv-title="getKeywordTitle(keyword, { pluralize: isFrenchLocale })"
    :location-title="locationTitle"
    :location-name="locationName"
    :keep-current-seo-content="keepCurrentSeoContent"
  >
    <TBreadcrumb
      v-if="breadcrumbs?.length"
      :items="breadcrumbs"
      class="mt-4 lg:justify-end"
    />
  </RvRentalsHero>

  <TContainer
    v-if="gridRvs?.length"
    size="xl"
    class="space-y-8"
  >
    <THeading
      tag="h2"
      :level="2"
    >
      {{ rvListTitle }}
    </THeading>

    <Grid
      :max-results="gridRvs?.length ?? pageSize"
      :sm-cols="2"
      :lg-cols="3"
      :rows="6"
      :sm-rows="4"
      :lg-rows="3"
    >
      <template #default="{ getItemClass }">
        <CardRv
          v-for="(rv, index) in gridRvs"
          :key="rv.Id"
          :index="index"
          search-request-id=""
          :rv="rv"
          no-lazy-load
          :eager-load="index < EAGER_LOAD_IMAGE_COUNT"
          cta="poi-rvs-grid"
          discovery-source="POI"
          :class="getItemClass(index)"
          is-seo-variant
          :image-preset="isMobile ? 'LANDSCAPE_MD' : 'LANDSCAPE_SM'"
          @visible:rv="queueViewableImpression"
        />
      </template>
    </Grid>

    <div class="flex justify-center">
      <TButton
        variant="highlight"
        class="w-full sm:w-auto"
        @click="handleSearchRVs"
      >
        {{ t('browseRVRentals', { rvTitle: getKeywordTitle(keyword, { casing: 'sentence' }) }) }}
      </TButton>
    </div>
  </TContainer>

  <!-- Discover section -->
  <div
    v-if="filteredContentSections?.[0]"
    class="my-24"
  >
    <TContainer size="2xl">
      <LazyVisibleRvRentalsCollapsibleBlock
        hydrate-on-visible
        :content-block="filteredContentSections[0]"
        variant="image-left"
      />
    </TContainer>
  </div>

  <TContainer size="xl">
    <RvRentalsHowToRent
      :keyword="keyword"
      :location-name="location?.name"
      :location-title="locationTitle"
      :location-title-short="location?.titleShort"
      :location-nearby="locationTitleNearby"
      :keep-current-seo-content="keepCurrentSeoContent"
      :rv-count="location?.stats?.RVCount"
      :min-price="location?.stats?.MinPrice ?? MIN_PRICE"
      :campground-count="location?.stats?.CampgroundCount"
      class="my-24"
      @show-more="handleSearchRVs"
    />
  </TContainer>

  <LazyVisibleRvRentalsBestReviews
    v-if="bestReviews?.length"
    hydrate-on-visible
    :rv-title="getKeywordTitle(keyword)"
    :location-title="locationTitle"
    :location-name="location.name ?? ''"
    :reviews="bestReviews"
    :keep-current-seo-content="keepCurrentSeoContent"
    :avg-rating="location?.stats?.AvgRating != null ? roundToNearestDecimal(location.stats.AvgRating) : undefined"
    :review-count="location?.stats?.ReviewCount"
  />

  <!-- Things to do section -->
  <div
    v-if="filteredContentSections?.[1]"
    class="my-24"
  >
    <TContainer size="2xl">
      <LazyVisibleRvRentalsCollapsibleBlock
        hydrate-on-visible
        :content-block="filteredContentSections[1]"
        variant="image-left"
      />
    </TContainer>
  </div>

  <TContainer
    v-if="nearbyEvents?.length"
    size="xl"
    class="my-24"
  >
    <LazyVisibleRvRentalsNearbyEvents
      hydrate-on-visible
      :location-title="locationTitle"
      :events="nearbyEvents"
    />
  </TContainer>

  <TContainer
    v-if="topHosts?.length >= 2"
    size="xl"
    class="my-24"
  >
    <LazyVisibleRvRentalsTopHostsSection
      hydrate-on-visible
      :hosts="topHosts"
      :location-title="locationTitle"
    />
  </TContainer>

  <TContainer
    v-if="nearbyNationalParks?.length"
    size="xl"
    class="my-24"
  >
    <LazyVisibleRvRentalsNearbyParks
      hydrate-on-visible
      :title="t('nationalParksTitle', { locationTitle: locationTitleNearby })"
      :parks="nearbyNationalParks"
    />
  </TContainer>

  <TContainer
    v-if="nearbyProvincialStateParks?.length"
    size="xl"
    class="my-24"
  >
    <LazyVisibleRvRentalsNearbyParks
      hydrate-on-visible
      :title="t(countryCode === 'CA' ? 'provincialParksTitle' : 'stateParksTitle', { locationTitle: locationTitleNearby })"
      :parks="nearbyProvincialStateParks"
    />
  </TContainer>

  <TContainer size="2xl">
    <div class="bg-green-50 max-sm:-mx-4 sm:relative sm:left-1/2 sm:-translate-x-1/2 sm:w-dvw xl:static xl:left-auto xl:translate-x-0 xl:w-auto">
      <TContainer size="xl">
        <LazyVisibleRvRentalsRvezyVsOthers
          hydrate-on-visible
          class="py-12 md:py-16"
        />
      </TContainer>
    </div>
  </TContainer>

  <TContainer
    v-if="nearbyCampgrounds?.length"
    size="xl"
    class="my-16"
  >
    <LazyVisibleRvRentalsCampgroundDelivery
      hydrate-on-visible
      :campgrounds="nearbyCampgrounds?.map((campground) => ({
        name: campground.Name,
        id: campground.Id,
      })) ?? []"
      :keep-current-seo-content="keepCurrentSeoContent"
      :location-nearby="locationTitleNearby"
      :campground-count="location?.stats?.CampgroundCount"
    />
  </TContainer>

  <div
    v-if="location?.coordinates?.latitude !== undefined && location?.coordinates?.longitude !== undefined && rvs?.results?.length"
    class="py-12 sm:py-16"
  >
    <TContainer
      v-if="keepCurrentSeoContent"
      size="xl"
    >
      <THeading
        :level="3"
        tag="h2"
        class="mb-8"
      >
        {{ t('rvsMaptitle', { rvTitle: getKeywordTitle(keyword, { pluralize: isFrenchLocale, casing: 'rvTitle' }), locationTitle }) }}
      </THeading>
    </TContainer>

    <TContainer size="2xl">
      <LazyVisibleRvRentalsMap
        hydrate-on-visible
        :center="{ lat: location.coordinates.latitude, lng: location.coordinates.longitude }"
        :bounds="bounds"
        :zoom="mapZoom"
        :rvs="rvs.results"
        :campgrounds="nearbyCampgrounds"
      />
    </TContainer>
  </div>

  <slot name="nearby-locations">
    <TContainer
      v-if="nearbyLocations?.length"
      size="xl"
      class="my-16"
    >
      <LazyVisibleGeneralNearbyLocations
        v-if="nearbyDestinations?.length"
        hydrate-on-visible
        :locations="nearbyDestinations"
        :distance-unit="distanceUnit"
        kind="RVRentals"
        :variant="nearbyDestinationsVariant"
        :location-with-preposition="locationTitleNearby"
        :rv-title="getKeywordTitle(keyword, { prefix: 'article' })"
        name-display="nameAndParent"
        :show-distance="isCity"
        :show-rv-count="!isCity"
        :title="keepCurrentSeoContent ? undefined : t('nearbyLocationsTitle', { locationNearby: locationTitleNearby, rvTitle: getKeywordTitle(keyword, { casing: 'sentence' }) })"
      />
    </TContainer>
  </slot>

  <LazyVisibleRvRentalsCommonSearches
    v-if="keywords?.length"
    hydrate-on-visible
    :location-title="locationTitle"
    :available-keywords="keywords"
    class="my-10 pb-10"
  />

  <div class="flex justify-center">
    <TButton
      variant="highlight"
      class="mx-4 w-full sm:w-auto"
      @click="handleSearchRVs"
    >
      {{ t('searchRVs', { rvTitle: getKeywordTitle(keyword, { pluralize: true }), locationTitle }) }}
    </TButton>
  </div>

  <TContainer
    size="xl"
    class="my-24"
  >
    <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
      <template #enabled>
        <GeneralBrandPillars focus="guest" />
      </template>
      <template #disabled>
        <GeneralBenefitsVariant />
      </template>
    </ExperimentComponent>
  </TContainer>

  <LazyVisibleRvRentalsFaqs
    v-if="faqs?.length"
    hydrate-on-visible
    :faqs="faqs"
    :rv-title="getKeywordTitle(keyword, { prefix: 'article' })"
    :location-title="locationTitle"
    class="my-16"
  />
</template>

<script setup lang="ts">
import { POINT_OF_INTEREST_TYPE } from '~/constants/point-of-interest'
import { type RV_TYPES, MOTORIZED_RV_TYPES, TOWABLE_RV_TYPES } from '~/constants/rv'
import { KEYWORD_TO_RV_TYPES, POI_RENTAL_TYPE_KEYWORDS } from '~/constants/keywords'
import { type COUNTRY_SHORTS, ROUTE_BASE_NAME } from '~/constants'
import type { RvRentalsLocation } from '~/types'
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import type { NearbyLocation } from '~/components/general/nearby-locations/types'
import type { RvRentalsFaq } from '~/components/rv-rentals/faqs/types'
import type { CampgroundSearchQueryRegion, PointOfInterest, PointOfInterestContentBlock, RvUnifiedSearchQuery, SeoEvent } from '~/types/rental-api-aliases'

const props = defineProps<{
  pointOfInterestId?: string
  location: RvRentalsLocation
  breadcrumbItems?: Breadcrumb[]
  contentBlocks?: PointOfInterestContentBlock[]
  faqs?: RvRentalsFaq[]
  nearbyLocations?: PointOfInterest[]
  availableKeywords?: string[]
}>()

const organizedContentBlocks = computed(() => {
  const raw = props.contentBlocks
  if (!raw) {
    return {}
  }

  return raw.reduce<Partial<Record<PointOfInterestContentBlock['BlockContentTypeId'], PointOfInterestContentBlock>>>((map, cb) => {
    if (!cb.BlockContentTypeId) {
      return map
    }

    map[cb.BlockContentTypeId] = cb

    return map
  }, {})
})

const filteredContentSections = computed(() => {
  const contentJson = organizedContentBlocks.value['PostRvResultsContent']?.ContentJson
  return contentJson?.filter((block) => Boolean(block.Content || block.Title)) ?? []
})

const { $captureError } = useNuxtApp()
const route = useRoute()
const { locale, t, te } = useI18n()
const localePath = useLocalePath()
const { getKeywordTitle } = usePointOfInterest()
const { geolocation, isInCaOrUs } = useGeolocation()
const { isMobile } = useDevice()

const isFrenchLocale = computed(() => locale.value === 'fr')

const isWorld = computed(() => props.location?.destinationType === POINT_OF_INTEREST_TYPE.World)
const isCountry = computed(() => props.location?.destinationType === POINT_OF_INTEREST_TYPE.Country)
const isRegion = computed(() => props.location?.destinationType === POINT_OF_INTEREST_TYPE.Region)
const isCity = computed(() => props.location?.destinationType === POINT_OF_INTEREST_TYPE.City)

const locationTitle = computed(() => props.location.title ?? t('nearYou'))
const locationTitleNearby = computed(() => props.location.titleNearby ?? t('nearYou'))
const locationName = computed(() => {
  if (isCountry.value) {
    return props.location?.name ?? ''
  }
  if (isRegion.value) {
    return [props.location?.name, props.location?.countryCode].filter(Boolean).join(', ')
  }
  return [props.location?.name, props.location?.regionCode].filter(Boolean).join(', ')
})

const keepCurrentSeoContent = computed(() => {
  if (isWorld.value || !props.location.title) {
    return true
  }

  return props.location.countryCode === 'CA'
})

const rvListTitle = computed(() => {
  if (keepCurrentSeoContent.value) {
    return t('rvsListTitle', { rvTitle: getKeywordTitle(keyword.value, { pluralize: true, casing: isFrenchLocale.value ? 'rvTitle' : undefined }), locationTitle: locationTitle.value })
  }

  return t('rvsListDestinationTitle', { n: props.location.stats?.RVCount ?? 0, locationTitle: locationTitle.value })
})

const keywords = computed(() => props.availableKeywords ?? [])

const nearbyDestinations = computed(() => (props?.nearbyLocations?.filter((location) => location.Indexable)?.map((location) => {
  const showParent = !isCountry.value
  const nameSuffix = showParent && location.ParentAttributes?.Abbreviation ? `, ${location.ParentAttributes?.Abbreviation}` : ''
  const name = location.Name ?? ''
  const nearbyLocation: NearbyLocation = {
    path: localePath({
      name: ROUTE_BASE_NAME.rv_rentals_destination,
      params: {
        keyword: route.params?.keyword,
        destinationPath: destinationPathSegments(location.CanonicalPath),
      },
    }),
    name,
    shortName: `${name}${nameSuffix}`,
    fullName: location.FullName ?? '',
    distanceKm: location.DistanceKM ?? null,
    rvCount: location.Stats?.RVCount ?? null,
  }
  return nearbyLocation
}) ?? []))

const pageSize = 9
async function useFetchRvs(query: object, keyword?: Nullable<string>) {
  if (keyword) {
    const { data } = await useRentals('/api/rvlistings/pre-filtered-search/{languageCode}/{keyword}',
      {
        path: {
          languageCode: locale.value,
          keyword,
        },
        query: {
          ...query,
          PageSize: 50,
        },
      },
    )

    return {
      rvs: {
        results: data.value?.SearchResults?.PopularRVs?.ListRVs ?? [],
        searchCriteria: data.value?.SearchCriteria,
      },
    }
  }
  else {
    const { data } = await useRentals('/api/rvlistings/unified-search', {
      query: {
        ...query,
        PageSize: 50,
      },
    })

    return {
      rvs: {
        results: data.value?.PopularRVs?.ListRVs ?? [],
        searchCriteria: undefined,
      },
    }
  }
}

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize,
})

function setSearchFromPointOfInterest(params: NonNullable<RvUnifiedSearchQuery>) {
  try {
    delete params.Region
    delete params.RegionId
    delete params.Country
    delete params.SearchLat
    delete params.SearchLng
    delete params.NeLat
    delete params.NeLng
    delete params.SwLat
    delete params.SwLng

    if (props.location?.coordinates) {
      if (isRegion.value) {
        params.RegionId = props.location.regionId
      }
      else if (isCountry.value) {
        params.Country = props.location.countryCode
      }
      else {
        params.SearchLat = props.location?.coordinates?.latitude
        params.SearchLng = props.location?.coordinates?.longitude
        params.Country = props.location.countryCode
      }
    }
  }
  catch (e) {
    $captureError(e)
  }

  return params
}

const { rvSearchApiRequestParams, navigateToRvSearch } = useSearch()
const commonFetchRvParams = computed(() => {
  let searchParams: NonNullable<RvUnifiedSearchQuery> = {
    ...rvSearchApiRequestParams.value,
  }

  if (!isWorld.value) {
    searchParams = setSearchFromPointOfInterest(searchParams)
  }

  return {
    ...searchParams,
    RVDescriptionNumChar: 200,
    PageSize: pageSize,
    IncludeFeatured: false,
  }
})

const mapZoom = computed(() => {
  if (isCountry.value) {
    return 4
  }
  else if (isRegion.value) {
    return 6
  }
  return 10
})

const keyword = computed(() => Array.isArray(route.params?.keyword) ? route.params?.keyword.join(' ') : route.params?.keyword)

/**
 * Breadcrumbs
 * RV rentals -> [[Keyword]] -> [BreadcrumbItems]
 */
const breadcrumbs = computed(() => {
  const items: Breadcrumb[] = []

  items.push({
    text: t('pageLink.rvRentals'),
    to: localePath({
      name: ROUTE_BASE_NAME.rv_rentals,
    }),
  })

  if (keyword.value) {
    items.push({
      text: getKeywordTitle(keyword.value, { casing: 'sentence' }) ?? '',
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_keyword,
        params: {
          keyword: keyword.value,
        },
      }),
    })
  }

  if (props.breadcrumbItems?.length) {
    items.push(...props.breadcrumbItems)
  }

  return items
})

const isCoordinatedLocation = computed(() => (isInCaOrUs.value && isWorld.value) || isCity.value)
const centerLatitude = computed(() => isCoordinatedLocation.value ? props.location.coordinates?.latitude : undefined)
const centerLongitude = computed(() => isCoordinatedLocation.value ? props.location.coordinates?.longitude : undefined)
const bounds = computed(() => isCoordinatedLocation.value
  ? undefined
  : {
      north: props.location.bounds?.North ?? undefined,
      south: props.location.bounds?.South ?? undefined,
      east: props.location.bounds?.East ?? undefined,
      west: props.location.bounds?.West ?? undefined,
    })

const regionName = computed(() => isRegion.value ? props.location.regionName : undefined)
const countryCode = computed(() => isInCaOrUs.value && isWorld.value ? geolocation.value?.countryCode as COUNTRY_SHORTS : props.location.countryCode as COUNTRY_SHORTS)

const AMOUNT_OF_REVIEWS = 9
async function fetchBestReviews() {
  const rvType = keyword.value && POI_RENTAL_TYPE_KEYWORDS.includes(keyword.value) ? KEYWORD_TO_RV_TYPES[keyword.value as keyof typeof KEYWORD_TO_RV_TYPES] : undefined

  // City or Geocoded location
  if (isCoordinatedLocation.value) {
    const { data: bestReviews } = await useRentals('/api/reviews/destination',
      {
        query: {
          latitude: centerLatitude.value,
          longitude: centerLongitude.value,
          top: AMOUNT_OF_REVIEWS,
          // make: rvs?.searchCriteria?.RVBrand ?? undefined,
          rvType,
        },
      })

    return bestReviews.value ?? []
  }

  // Region or Country
  const { data: bestReviews } = await useRentals('/api/reviews/rv', {
    query: {
      country: countryCode.value,
      region: regionName.value,
      top: AMOUNT_OF_REVIEWS,
      // make: rvs?.searchCriteria?.RVBrand ?? undefined,
      rvType,
    },
  })

  return bestReviews.value ?? []
}

async function fetchTopHosts() {
  if (!props.location?.name && (!isCity.value && !isRegion.value)) {
    return []
  }

  const { data } = await useRentals('/api/hosts/top-hosts', {
    query: {
      ...commonFetchRvParams.value,
      SearchCity: isCity.value ? props.location.nameEn ?? props.location.name : undefined,
      SearchRegion: isCountry.value ? undefined : props.location.regionName,
      Limit: 2,
    },
  })

  return data.value ?? []
}

async function fetchNearbyCampgrounds() {
  const { data } = await useRentals('/api/campgrounds', {
    query: {
      PageSize: 10,
      Latitude: isCoordinatedLocation.value ? props.location.coordinates?.latitude ?? undefined : undefined,
      Longitude: isCoordinatedLocation.value ? props.location.coordinates?.longitude ?? undefined : undefined,
      Region: isRegion.value ? props.location.regionCode as CampgroundSearchQueryRegion : undefined,
      Country: isInCaOrUs.value ? props.location.countryCode as COUNTRY_SHORTS : undefined,
      Language: lookupLocaleToLanguage(locale.value),
    },
  })

  return data.value?.Data ?? []
}

async function fetchNearbyProvincialStateParks() {
  const { data } = await useRentals('/api/parks', {
    query: {
      CenterLatitude: centerLatitude.value,
      CenterLongitude: centerLongitude.value,
      Country: countryCode.value,
      Region: regionName.value,
      RadiusKm: isCoordinatedLocation.value ? 100 : undefined,
      PageSize: 4,
      HasImage: true,
      PointOfInterestId: props.pointOfInterestId,
      LanguageCode: locale.value,
      ParkType: countryCode.value === 'CA' ? 'Provincial' : 'State',
    },
  })

  return data.value?.Data ?? []
}

async function fetchNearbyNationalParks() {
  const { data } = await useRentals('/api/parks', {
    query: {
      CenterLatitude: centerLatitude.value,
      CenterLongitude: centerLongitude.value,
      Region: regionName.value,
      Country: countryCode.value,
      PageSize: 4,
      HasImage: true,
      PointOfInterestId: props.pointOfInterestId,
      LanguageCode: locale.value,
      ParkType: 'National',
      SortBy: 'Distance',
    },
  })

  return data.value?.Data ?? []
}

async function fetchNearbyEvents(): Promise<SeoEvent[]> {
  if (!isCity.value && !isRegion.value) {
    return []
  }

  const latitude = isCity.value ? props.location?.coordinates?.latitude : undefined
  const longitude = isCity.value ? props.location?.coordinates?.longitude : undefined

  const { data } = await useRentals('/api/events', {
    query: {
      CenterLatitude: latitude,
      CenterLongitude: longitude,
      Region: isRegion.value ? props.location?.regionName : undefined,
      Country: props.location?.countryCode as COUNTRY_SHORTS,
      LanguageCode: locale.value,
      RadiusKm: latitude && longitude ? 100 : undefined,
      PageSize: 3,
    },
  })

  return data.value?.Data ?? []
}

/**
 * Fetch RVs alongside top hosts, campgrounds, parks, events and best reviews in parallel, since none of them
 * depend on each other.
 */
const [{ rvs }, topHosts, nearbyCampgrounds, nearbyNationalParks, nearbyProvincialStateParks, nearbyEvents, bestReviews] = await Promise.all([
  useFetchRvs(commonFetchRvParams.value, keyword.value),
  fetchTopHosts(),
  fetchNearbyCampgrounds(),
  fetchNearbyNationalParks(),
  fetchNearbyProvincialStateParks(),
  fetchNearbyEvents(),
  fetchBestReviews(),
])

const gridRvs = rvs?.results?.slice(0, pageSize) ?? []

/**
 * Search for more RVs
 */
const searchData = useSearchData()
async function handleSearchRVs() {
  const criteria = rvs?.searchCriteria

  // We only support RVTypes and RVBrand for now
  const Types = ([...MOTORIZED_RV_TYPES, ...TOWABLE_RV_TYPES] as const).filter(
    (type): type is RV_TYPES => Boolean(
      type === 'TravelTrailer' ? criteria?.SubTravelTrailer : criteria?.[type],
    ),
  )

  await navigateToRvSearch({
    PlaceId: searchData.value.location?.placeId ?? undefined,
    Types,
    RVBrand: criteria?.RVBrand ?? undefined,
  })
}

const distanceUnit = computed(() => countryCodeToDistanceUnit(
  props.location?.countryCode,
))

/**
 * Metadata
 */
const MIN_PRICE = 47
const locationWithPreposition = computed(() => locationTitle.value === t('nearYou') && !isFrenchLocale.value ? formatAsTitleCase(locationTitle.value) : locationTitle.value)
const { priceDisplay } = usePrice()

const title = computed(() => {
  if (organizedContentBlocks.value['MetaTitle']?.Content) {
    return organizedContentBlocks.value['MetaTitle'].Content
  }

  let rvType = t('common.rv')
  let make = ''

  if (keyword.value) {
    const isRvTypeKeyword = POI_RENTAL_TYPE_KEYWORDS.includes(keyword.value)
    if (isRvTypeKeyword) {
      rvType = getKeywordTitle(keyword.value, { casing: isFrenchLocale.value && props.location?.title ? undefined : 'sentence', pluralize: isFrenchLocale.value && !props.location?.title })
    }
    else {
      make = `${getKeywordTitle(keyword.value, { casing: 'title' })} `
    }
  }
  else if (keepCurrentSeoContent.value) {
    // No keyword in Canada, so we use the default title
    return t('metadata.title.default', { locationWithPreposition: locationWithPreposition.value })
  }

  const baseTitle = props.location?.title
    ? t('metadata.title.destination', { locationName: props.location.name, locationWithPrepositionShort: props.location.titleShort, make, rvType })
    : t('metadata.title.nearby', { make, rvType })

  return buildDestinationPageMetadataTitle({
    baseTitle,
    rvsFrom: t('metadata.title.rvsFrom', { minPrice: priceDisplay({ value: props.location.stats?.MinPrice ?? MIN_PRICE, round: true }) }),
    avgRating: roundToNearestDecimal(props.location.stats?.AvgRating ?? 0),
    reviewCount: props.location.stats?.ReviewCount ?? 0,
    rvCount: props.location.stats?.RVCount ?? 0,
  })
})

const description = computed(() => {
  if (organizedContentBlocks.value['MetaDescription']?.Content) {
    return organizedContentBlocks.value['MetaDescription'].Content
  }

  let rvType = isFrenchLocale.value ? t('common.rvs') : t('common.rv')
  let makePaddedEnd = ''
  let makePaddedStart = ''
  let callout = t('metadata.description.callout.default')

  if (keyword.value) {
    if (te(`metadata.description.callout.${keyword.value}`)) {
      callout = t(`metadata.description.callout.${keyword.value}`)
    }

    const isRvTypeKeyword = POI_RENTAL_TYPE_KEYWORDS.includes(keyword.value)
    if (isRvTypeKeyword) {
      rvType = getKeywordTitle(keyword.value, { casing: 'sentence' })
    }
    else {
      const make = getKeywordTitle(keyword.value, { casing: 'title' })
      makePaddedEnd = `${make} `
      makePaddedStart = ` ${make}`
    }
  }
  else if (keepCurrentSeoContent.value) {
    // No keyword in Canada, so we use the default description
    return t('metadata.description.default', { locationWithPreposition: locationWithPreposition.value, callout })
  }

  const baseDescription = props.location?.title
    ? t('metadata.description.destination', { locationWithPreposition: locationWithPreposition.value, make: makePaddedEnd, rvType, minPrice: priceDisplay({ value: props.location.stats?.MinPrice ?? MIN_PRICE, round: true }), callout })
    : t('metadata.description.nearby', { make: isFrenchLocale.value ? makePaddedStart : makePaddedEnd, rvType, callout })

  return buildDestinationPageMetadataDescription({
    baseDescription,
    rvCount: props.location.stats?.RVCount ?? 0,
    delivery: props.location.stats?.CampgroundCount ? t('metadata.description.deliveredToCampgrounds', { n: props.location.stats.CampgroundCount }) : t('metadata.description.deliveryAvailable'),
    bookInstantly: t('metadata.description.bookInstantly'),
  })
})

useSeoHeadTags({
  title,
  titleTemplate: organizedContentBlocks.value['MetaTitle']?.Content ? undefined : null,
  description,
})

const { defineVehicle } = useAugmentedSchemaOrg()
const { getDynamicImage, presets } = useImage()
const absoluteLocalePath = useAbsoluteLocalePath()

const imagePreset = computed(() => isMobile ? presets.LANDSCAPE_MD : presets.LANDSCAPE_SM)

const EAGER_LOAD_IMAGE_COUNT = 3
useHead({
  link: gridRvs.slice(0, EAGER_LOAD_IMAGE_COUNT).flatMap((rv) => {
    const path = rv.Photos?.[0]?.Path
    if (!path) {
      return []
    }

    const href = getDynamicImage({ path, preset: imagePreset.value })
    if (!href) {
      return []
    }

    return [{
      rel: 'preload',
      as: 'image',
      fetchpriority: 'high',
      href,
    }]
  }),
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbs.value,
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),

  defineWebPage({
    '@type': 'CollectionPage',
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': gridRvs?.map((rv, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'item': defineVehicle({
          rvName: rv.RVName!,
          rvMake: rv.Make,
          rvCity: rv.City,
          rvRegion: rv.State,
          rvCountry: rv.Country as COUNTRY_SHORTS,
          rvType: rv.RVType,
          rvGuests: rv.Guests,
          rvYear: rv.Year,
          rvDefaultPrice: rv.DefaultPrice,
          url: absoluteLocalePath(generateRvPath(rv.AliasName ?? '')),
          images: rv.Photos?.filter((photo) => photo.Path).map((photo) => getDynamicImage({
            path: photo.Path,
            preset: imagePreset.value,
          }) ?? '') ?? [],
        }),
      })) ?? [],
    },
    // Google Review snippet doesn't support aggregateRating on CollectionPage.
    // ...((props.location.stats?.AvgRating ?? 0) > 3.5 && (props.location.stats?.ReviewCount ?? 0) > 100
    //   ? {
    //       aggregateRating: {
    //         '@type': 'AggregateRating',
    //         'ratingValue': props.location.stats?.AvgRating,
    //         'reviewCount': props.location.stats?.ReviewCount,
    //         'bestRating': 5,
    //       },
    //     }
    //   : {}),
  }),
])

const nearbyDestinationsVariant = computed(() => {
  if (isCountry.value) {
    const regionType = props.location.regionType
      ?? (props.location.countryCode
        ? lookupRegionTypeByCountryCode(props.location.countryCode as COUNTRY_SHORTS)
        : undefined)

    return regionType === 'province' ? 'province' : 'state'
  }

  if (isCity.value) {
    return 'city'
  }

  return 'default'
})
</script>

<i18n src="~~/i18n/locales/common/nearby-locations.json" lang="json" />

<i18n lang="json">
  {
    "en": {
      "metadata": {
        "title": {
          "default": "RV Rentals {locationWithPreposition} {'|'} RVezy",
          "destination": "{locationName} {make}{rvType} Rentals {'-'}",
          "nearby": "The Best {make}{rvType} Rentals Near You {'-'}",
          "rvsFrom": "RVs from {minPrice}/night"
        },
        "description": {
          "default": "Find affordable RV rentals {locationWithPreposition} ✓ Fully insured ✓ Delivery available ✓ Pet-friendly ✓ Trusted by travelers across North America",
          "destination": "{make}{rvType} rentals {locationWithPreposition} {'-'} {callout} from {minPrice}/night.",
          "nearby": "{make}{rvType}, {callout} for rent near you. Compare motorhomes and camper vans.",
          "deliveryAvailable": "Delivery available.",
          "deliveredToCampgrounds": "Delivered to nearby campgrounds. | Delivered to a nearby campground. | Delivered to {n} nearby campgrounds.",
          "bookInstantly": "Book instantly.",
          "callout": {
            "default": "campers, travel trailers and motorhomes",
            "motorhome": "Class A, Class B and Class C RVs",
            "class-a": "motorhomes and luxury coaches",
            "class-b": "camper vans and Sprinter conversions",
            "campervan": "Class B RVs and Sprinter conversions",
            "class-c": "motorhomes sleeping 4 to 8",
            "travel-trailer": "small and luxury camper trailers",
            "fifth-wheel": "5th wheel campers and toy haulers",
            "trailer": "pop-up campers and toy haulers",
            "micro-trailer": "teardrop trailers and mini campers",
            "toy-hauler": "campers with a built-in garage",
            "airstream": "Basecamp, Bambi and Flying Cloud trailers"
          }
        }
      },

      "rvsListTitle": "Rental {rvTitle} {locationTitle}",
      "rvsListDestinationTitle": "RVs for rent {locationTitle} | 1 RV for rent {locationTitle} | {n} RVs for rent {locationTitle}",
      "browseRVRentals": "Browse {rvTitle} rentals",
      "searchRVs": "Search {rvTitle} {locationTitle}",
      "rvsMaptitle": "{rvTitle} rentals {locationTitle}",
      "nationalParksTitle": "Best National Parks {locationTitle}",
      "provincialParksTitle": "Popular Provincial Parks {locationTitle}",
      "stateParksTitle": "Popular State Parks {locationTitle}",
      "nearbyLocationsTitle": "{rvTitle} rentals {locationNearby}"
    },

    "fr": {
      "metadata": {
        "title": {
          "default": "Meilleurs VRs à louer {locationWithPreposition} {'|'} RVezy",
          "destination": "Location de {rvType} {make}{locationWithPrepositionShort} {'-'}",
          "nearby": "{rvType} {make}à louer près de chez vous {'-'}",
          "rvsFrom": "VRs à partir de {minPrice}/nuit"
        },
        "description": {
          "default": "Réserver les meilleurs VRs à louer {locationWithPreposition} ✓ Entièrement assuré ✓ Livraison disponible ✓ Animaux acceptés",
          "destination": "{rvType} {make}à louer {locationWithPreposition} {'-'} {callout} à partir de {minPrice}/nuit.",
          "nearby": "{rvType}{make}, location de {callout} près de chez vous. Comparez les motorisés et les campervans.",
          "deliveryAvailable": "Livraison disponible.",
          "deliveredToCampgrounds": "Livraison aux campings à proximité. | Livraison à un camping à proximité. | Livraison à {n} campings à proximité.",
          "bookInstantly": "Réserver instantanément.",
          "callout": {
            "default": "campers, roulottes et motorisés",
            "motorise": "VRs Classe A, Classe B et Classe C",
            "classe-a": "motorisés et coachs de luxe",
            "classe-b": "campervans et conversions Sprinter",
            "campervan": "VRs Classe B et conversions Sprinter",
            "classe-c": "motorisés Classe C de 4 à 8 places",
            "caravane-de-voyage": "roulottes compactes et luxueuses",
            "sellette": "roulottes à sellette et roulottes avec garage",
            "roulotte": "remorques pop-up et roulottes avec garage",
            "mini-roulotte": "roulottes teardrop et mini campers",
            "roulotte-cargo": "roulottes avec garage intégré",
            "airstream": "Basecamp, Bambi et roulottes Flying Cloud"
          }
        }
      },

      "rvsListTitle": "{rvTitle} à louer {locationTitle}",
      "rvsListDestinationTitle": "VRs à louer {locationTitle} | 1 VR à louer {locationTitle} | {n} VRs à louer {locationTitle}",
      "browseRVRentals": "Parcourir les locations de {rvTitle}",
      "searchRVs": "Parcourir les {rvTitle} {locationTitle}",
      "rvsMaptitle": "{rvTitle} à louer {locationTitle}",
      "nationalParksTitle": "Meilleurs parcs nationaux {locationTitle}",
      "provincialParksTitle": "Parcs provinciaux populaires {locationTitle}",
      "stateParksTitle": "Parcs d'État populaires {locationTitle}",
      "nearbyLocationsTitle": "{rvTitle} à louer {locationNearby}"
    }
  }
</i18n>
